import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { MapPin, Building, Waves, Music, CheckCircle } from 'lucide-react';

const PattayaSolutions = () => {
  const { t } = useTranslation();
  const sp = (key) => t(`service_pages.pattaya_solutions.${key}`);
  const common = (key) => t(`service_pages.common.${key}`);
  const solutions = t('features.pattaya_solutions_features', { returnObjects: true });

  return (
    <>
      <Helmet>
        <title>{sp('meta_title')}</title>
        <meta name="description" content={sp('meta_desc')} />
        <meta name="keywords" content="Pattaya software development, Pattaya business solutions, hotel system Pattaya, tour booking Pattaya, club management software, Pattaya website" />
        <link rel="canonical" href="https://karnidigitalsolutions.com/services/pattaya-solutions" />
      </Helmet>

      <section className="pt-24 pb-16 bg-gradient-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6" data-aos="fade-up">
              {sp('title')}
            </h1>
            <p className="text-xl md:text-2xl text-gold mb-8 max-w-3xl mx-auto" data-aos="fade-up" data-aos-delay="200">
              {sp('subtitle')}
            </p>
            <div className="flex flex-wrap gap-4 justify-center" data-aos="fade-up" data-aos-delay="400">
              <Link to="/contact" className="btn-gold">{common('get_quote')}</Link>
              <a href="https://wa.me/66968826252" className="btn-primary" target="_blank" rel="noopener noreferrer">
                {common('whatsapp_us')}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6 text-center">
            {sp('section_title')}
          </h2>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            {sp('section_desc')}
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutions.map((solution, index) => (
              <div key={index} className="flex items-start space-x-3" data-aos="fade-up" data-aos-delay={index * 50}>
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700 font-medium">{solution}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
            {sp('industries_title')}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="card bg-white text-center" data-aos="fade-up">
              <Building className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold text-primary mb-2">{t('service_pages.pattaya_solutions.hotels_resorts')}</h3>
              <p className="text-gray-600">{t('service_pages.pattaya_solutions.booking_systems_management')}</p>
            </div>
            
            <div className="card bg-white text-center" data-aos="fade-up" data-aos-delay="100">
              <Waves className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold text-primary mb-2">Tours & Travel</h3>
              <p className="text-gray-600">Booking portals & packages</p>
            </div>
            
            <div className="card bg-white text-center" data-aos="fade-up" data-aos-delay="200">
              <Music className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold text-primary mb-2">Clubs & Bars</h3>
              <p className="text-gray-600">Table booking & POS systems</p>
            </div>
            
            <div className="card bg-white text-center" data-aos="fade-up" data-aos-delay="300">
              <MapPin className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold text-primary mb-2">Real Estate</h3>
              <p className="text-gray-600">Property portals & CRM</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-primary-50 rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6 text-center">
              🏆 {sp('rank_title')}
            </h2>
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-primary mb-2">10+</div>
                <div className="text-gray-700">Years Experience</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary mb-2">250+</div>
                <div className="text-gray-700">Projects Delivered</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary mb-2">6</div>
                <div className="text-gray-700">Languages Supported</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-gold">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            {sp('cta_title')}
          </h2>
          <p className="text-xl text-primary-700 mb-8">
            {sp('cta_desc')}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="btn-primary">{common('request_quote')}</Link>
            <a href="tel:+66968826252" className="btn-outline">{common('call_now')}</a>
          </div>
        </div>
      </section>
    </>
  );
};

export default PattayaSolutions;
